import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { campaignApi } from '../services/api';
import { Campaign, CampaignStats, CampaignStatus } from '../types';
import { fmtDay, istDay, istToday, pct } from '../lib';
import DateRangeBar from '../components/DateRangeBar';
import StatStrip from '../components/StatStrip';
import { Dash, Empty, Failed, Loading, PageFrame, Section, StatusChip, Strip, Tabs, Th, td, trClick } from '../components/PlatformBits';

type StatusFilter = '' | CampaignStatus;
type SortKey = 'started' | 'visits' | 'scheduled' | 'enrolled' | 'sent' | 'read' | 'replied' | 'clicked' | 'failed';

const STATUS_ORDER: CampaignStatus[] = ['Active', 'Paused', 'Draft', 'Completed', 'Aborted'];

const STAT_OF: Record<Exclude<SortKey, 'started'>, (s: CampaignStats) => number> = {
  visits: (s) => s.visits_conducted,
  scheduled: (s) => s.visited,
  enrolled: (s) => s.enrolled,
  sent: (s) => s.sent,
  read: (s) => s.read,
  replied: (s) => s.replied,
  clicked: (s) => s.clicked,
  failed: (s) => s.failed,
};

/** Sum of many campaigns' stats, shaped like one campaign's. */
const total = (list: Campaign[]): CampaignStats => {
  const t: CampaignStats = {
    enrolled: 0, excluded: 0, scheduled: 0, sent: 0, delivered: 0, read: 0, replied: 0, clicked: 0,
    visited: 0, failed: 0, skipped: 0, visits_conducted: 0, error_codes: {}, attempts: {}, by_stage: [],
  };
  list.forEach(({ stats: s }) => {
    if (!s) return;
    (Object.keys(t) as (keyof CampaignStats)[]).forEach((k) => {
      if (typeof t[k] === 'number') (t as any)[k] += s[k] as number;
    });
  });
  return t;
};

/** A count with its % of enrolled beside it; a dash for zero. */
const Rate: React.FC<{ v: number; of: number }> = ({ v, of }) => (
  <td className={`${td} text-right`}>
    {v ? <>{v}<span className="ml-1.5 text-[11.5px] text-graphite">{pct(v, of)}</span></> : <Dash />}
  </td>
);

const CampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  // Inclusive IST-day window on campaign start; defaults to all of them.
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [status, setStatus] = useState<StatusFilter>('');
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'started', desc: true });
  const [error, setError] = useState<string | null>(null);

  const allTime = useCallback((list: Campaign[]) => ({
    from: list.length ? list.map((c) => istDay(c.created_at)).sort()[0] : istToday(),
    to: istToday(),
  }), []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const list = await campaignApi.listWithStats();
      setCampaigns(list);
      setRange((r) => r ?? allTime(list));
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message);
    }
  }, [allTime]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const inWindow = useMemo(() => (campaigns && range)
    ? campaigns.filter((c) => { const d = istDay(c.created_at); return d >= range.from && d <= range.to; })
    : [], [campaigns, range]);

  const rows = useMemo(() => {
    const list = inWindow.filter((c) => !status || c.status === status);
    const val = (c: Campaign): number | string => sort.key === 'started'
      ? c.created_at
      : (c.stats ? STAT_OF[sort.key](c.stats) : -1);
    return [...list].sort((a, b) => {
      const x = val(a), y = val(b);
      const cmp = x < y ? -1 : x > y ? 1 : b.created_at.localeCompare(a.created_at);
      return sort.desc ? -cmp : cmp;
    });
  }, [inWindow, status, sort]);

  if (error) return <Failed what="campaigns" error={error} />;
  if (campaigns === null || range === null) return <div className="px-10 pt-8"><Loading what="campaigns" /></div>;

  // Status tabs only for statuses present in the window; hidden when there is only one.
  const present = STATUS_ORDER.filter((s) => inWindow.some((c) => c.status === s));
  const statusTabs: [StatusFilter, React.ReactNode][] = [
    ['', <>All <span className="num opacity-70">{inWindow.length}</span></>],
    ...present.map((s): [StatusFilter, React.ReactNode] =>
      [s, <>{s} <span className="num opacity-70">{inWindow.filter((c) => c.status === s).length}</span></>]),
  ];
  const showFailed = rows.some((c) => (c.stats?.failed ?? 0) > 0);
  const cols = 8 + (showFailed ? 1 : 0);
  const open = (c: Campaign) => navigate(`/whatsapp/${c.id}`);

  const sortTh = (k: SortKey, label: string, o: { ours?: boolean; left?: boolean } = {}) => {
    const on = sort.key === k;
    return (
      <Th ours={o.ours} left={o.left}>
        <button
          onClick={() => setSort(on ? { key: k, desc: !sort.desc } : { key: k, desc: true })}
          title={`Sort by ${label.toLowerCase()}`}
          className={`inline-flex items-center gap-1 uppercase tracking-[0.06em] hover:text-charcoal focus:outline-none focus-visible:underline ${on && !o.ours ? 'text-charcoal' : ''}`}
        >
          {label}
          <span aria-hidden className={`text-[9px] ${on ? '' : 'opacity-0'}`}>{on && !sort.desc ? '▲' : '▼'}</span>
        </button>
      </Th>
    );
  };

  const emptyText = campaigns.length === 0
    ? 'No campaigns yet. They appear here once one is created with run-campaign.'
    : inWindow.length === 0
      ? <>No campaigns started in this window. <button onClick={() => setRange(allTime(campaigns))} className="ml-1 font-semibold text-forest hover:underline underline-offset-2">Show all time</button></>
      : <>No {status.toLowerCase()} campaigns in this window. <button onClick={() => setStatus('')} className="ml-1 font-semibold text-forest hover:underline underline-offset-2">Show all</button></>;

  return (
    <PageFrame
      title="WhatsApp"
      subline="Broadcast campaigns — site visits they produced, then how the send landed."
      bar={<DateRangeBar from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })}
                         caption={`${inWindow.length} of ${campaigns.length} campaigns, by start day`} />}
    >
      <Strip><StatStrip stats={total(inWindow)} /></Strip>

      <Section
        title="Campaigns"
        note={present.length > 1
          ? <Tabs value={status} onChange={setStatus} items={statusTabs} />
          : `${rows.length} campaign${rows.length === 1 ? '' : 's'}`}
      >
        <table className="w-full min-w-[960px] border-collapse">
          <thead><tr>
            {sortTh('started', 'Campaign', { left: true })}
            {sortTh('visits', 'Site visits', { ours: true })}
            {sortTh('scheduled', 'Scheduled', { ours: true })}
            {sortTh('enrolled', 'Enrolled')}
            {sortTh('sent', 'Sent')}
            {sortTh('read', 'Read')}
            {sortTh('replied', 'Replied')}
            {sortTh('clicked', 'Clicked')}
            {showFailed && sortTh('failed', 'Failed')}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <Empty cols={cols}>{emptyText}</Empty>}
            {rows.map((c) => {
              const s = c.stats;
              return (
                <tr
                  key={c.id}
                  tabIndex={0}
                  onClick={() => open(c)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(c); } }}
                  className={`${trClick(false)} align-middle focus:outline-none focus-visible:bg-sage/20 group`}
                >
                  <td className="px-3 py-3 max-w-[380px]">
                    <div className="flex items-center gap-2 min-w-0">
                      <Link to={`/whatsapp/${c.id}`} tabIndex={-1} onClick={(e) => e.stopPropagation()}
                            className="font-semibold text-charcoal group-hover:text-forest truncate">{c.name}</Link>
                      <StatusChip status={c.status} />
                    </div>
                    <div className="text-[11.5px] text-graphite mt-0.5 num">started {fmtDay(c.created_at)}</div>
                  </td>
                  {s ? <>
                    <td className={`${td} text-right`}>
                      {s.visits_conducted
                        ? <span className="text-forest font-semibold text-[16px]">{s.visits_conducted}</span>
                        : <Dash />}
                    </td>
                    <td className={`${td} text-right`}>
                      {s.visited ? <span className="text-amber-ink font-semibold">{s.visited}</span> : <Dash />}
                    </td>
                    <td className={`${td} text-right font-semibold`}>{s.enrolled || <Dash />}</td>
                    <Rate v={s.sent} of={s.enrolled} />
                    <Rate v={s.read} of={s.enrolled} />
                    <Rate v={s.replied} of={s.enrolled} />
                    <Rate v={s.clicked} of={s.enrolled} />
                    {showFailed && (
                      <td className={`${td} text-right`}>{s.failed ? <span className="text-brick font-semibold">{s.failed}</span> : <Dash />}</td>
                    )}
                  </> : <td colSpan={cols - 1} className={`${td} text-graphite text-[12.5px]`}>no stats yet</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>
    </PageFrame>
  );
};

export default CampaignsPage;
